import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import { CssBaseline, Box, Alert } from '@mui/material'
import '@fontsource/poppins/300.css'
import '@fontsource/poppins/400.css'
import '@fontsource/poppins/500.css'
import '@fontsource/poppins/600.css'
import '@fontsource/poppins/700.css'

import theme from './theme'
import config, { getRouterBasename } from './config'
import { getCompanyPublicData, getCompanyBySlug } from './services/api'

import Header from './components/Header'
import Footer from './components/Footer'
import LoadingSpinner from './components/LoadingSpinner'

import Home from './pages/Home'
import Services from './pages/Services'
import Bookings from './pages/Bookings'
import Reviews from './pages/Reviews'
import FAQ from './pages/FAQ'
import ConfirmBooking from './pages/ConfirmBooking'
import CancelBooking from './pages/CancelBooking'
import Unsubscribe from './pages/Unsubscribe'
import BookingConfirmation from './pages/BookingConfirmation'

const MAIN_SITE_URL = 'https://mygroupbooker.com'

function PreviewBanner() {
  return (
    <Alert
      severity='warning'
      sx={{
        borderRadius: 0,
        justifyContent: 'center',
        '& .MuiAlert-message': {
          textAlign: 'center',
        },
      }}
    >
      <strong>Preview Mode</strong> - This site is not yet public. Only you can see this preview.
    </Alert>
  )
}

function App() {
  const [companyData, setCompanyData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isPreviewMode, setIsPreviewMode] = useState(config.isPreviewMode)

  // Update favicon when company data loads
  useEffect(() => {
    if (!companyData?.company?.avatar) return

    const avatar = companyData.company.avatar
    const logoUrl = avatar.imageMedium || avatar.image || avatar.imageThumbnail
    if (!logoUrl) return

    // Make URL absolute if needed
    const fullUrl = logoUrl.startsWith('/') ? `${config.server}${logoUrl}` : logoUrl

    // Update the favicon
    let link = document.querySelector("link[rel~='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = fullUrl

    // Also update apple-touch-icon
    let appleLink = document.querySelector("link[rel='apple-touch-icon']")
    if (!appleLink) {
      appleLink = document.createElement('link')
      appleLink.rel = 'apple-touch-icon'
      document.head.appendChild(appleLink)
    }
    appleLink.href = fullUrl
  }, [companyData])

  useEffect(() => {
    // Redirect to main site if on book domain without a valid slug
    if (config.noValidSlug) {
      window.location.replace(MAIN_SITE_URL)
      return
    }

    const fetchData = async () => {
      try {
        setLoading(true)

        let companyId = config.companyId

        // If we need to resolve company by slug first
        if (config.needsSlugResolution && config.companySlug) {
          const slugData = await getCompanyBySlug(
            config.companySlug,
            config.previewToken
          )

          // Check if site is accessible
          // API returns camelCase: siteAccessible, companyId, isPreviewMode
          if (!slugData.siteAccessible) {
            setError('This booking site is not currently available.')
            setLoading(false)
            return
          }

          companyId = slugData.companyId
          setIsPreviewMode(slugData.isPreviewMode)
        }

        const data = await getCompanyPublicData(companyId)
        setCompanyData(data)
        setError(null)
      } catch (err) {
        console.error('Error loading company data:', err)

        // Redirect to main site if slug/company not found
        if (err.response?.status === 404) {
          window.location.replace(MAIN_SITE_URL)
          return
        }

        setError(
          'Unable to load company data. Please check your configuration.'
        )
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <LoadingSpinner />
        </Box>
      </ThemeProvider>
    )
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            textAlign: 'center',
            p: 3,
          }}
        >
          <div>
            <h2>Error Loading Application</h2>
            <p>{error}</p>
            <p style={{ fontSize: '0.9em', color: '#666' }}>
              Company ID: {config.companyId}
              <br />
              API Endpoint: {config.api.endpoint}
            </p>
          </div>
        </Box>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router basename={getRouterBasename()}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
          }}
        >
          {isPreviewMode && <PreviewBanner />}
          <Header
            companyName={companyData?.company?.name}
            companyAvatar={companyData?.company?.avatar}
          />
          <Box component='main' sx={{ flexGrow: 1 }}>
            <Routes>
              <Route path='/' element={<Home companyData={companyData} />} />
              <Route
                path='/services'
                element={<Services companyData={companyData} />}
              />
              <Route
                path='/bookings'
                element={<Bookings companyData={companyData} />}
              />
              <Route
                path='/reviews'
                element={<Reviews companyData={companyData} />}
              />
              <Route path='/faq' element={<FAQ companyData={companyData} />} />
              <Route
                path='/booking-confirmation'
                element={<BookingConfirmation companyData={companyData} />}
              />
              <Route
                path='/confirm-booking/:token'
                element={<ConfirmBooking />}
              />
              <Route
                path='/cancel-booking/:token'
                element={<CancelBooking />}
              />
              <Route
                path='/unsubscribe/:token'
                element={<Unsubscribe />}
              />
            </Routes>
          </Box>
          <Footer company={companyData?.company} />
        </Box>
      </Router>
    </ThemeProvider>
  )
}

export default App
